import { YStack, isWeb, Text, Paragraph } from 'tamagui'
import { Input } from '@components/Input'
import { Button } from '@components/Button'
import { GuestCard } from '@components/GuestCard'
import { PasswordStrengthMeter } from '@components/PasswordStrengthMeter'
import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useToastController } from '@tamagui/toast'
import { router, useLocalSearchParams } from 'expo-router'
import { useSession } from '@hooks/useSession'
import { AUTH_TOKEN } from '@constants/storages'
import { useAsyncStorage } from '@react-native-async-storage/async-storage'
import { KeyboardAvoidingView, Platform, ActivityIndicator } from 'react-native'
import { useThemeParams, useHasFeature } from '@hooks/useThemeParams'
import { STRONG_PASSWORD_POLICY } from '@constants/features'
import { LoadingScreen } from '@components/LoadingScreen'
import { useUserPasswordReset } from '@services/api/graphql/mutations/userPasswordReset'
import { faKey } from '@fortawesome/pro-light-svg-icons/faKey'

const MIN_PASSWORD_LENGTH = 6

type InvalidReason = 'invalid_token' | 'expired_token'

const goToLogin = () => {
  if (isWeb) {
    window.location.href = '/users/sign_in'
  } else {
    router.replace('/users/sign_in')
  }
}

const goToPasswordRequest = () => {
  if (isWeb) {
    window.location.href = '/users/password/new'
  } else {
    router.replace('/users/password/new')
  }
}

export default function PasswordReset() {
  const { t } = useTranslation('PasswordReset')
  const { reset_password_token: resetPasswordToken } = useLocalSearchParams<{
    reset_password_token?: string
  }>()
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [serverErrors, setServerErrors] = useState<string[]>([])
  const [invalidReason, setInvalidReason] = useState<InvalidReason | null>(
    resetPasswordToken ? null : 'invalid_token',
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  // O initializer do useState só vê o primeiro render: se o search param
  // chegar depois, a tela ficaria travada em "link inválido" com token válido.
  useEffect(() => {
    if (!resetPasswordToken) return

    setInvalidReason((previous) =>
      previous === 'invalid_token' ? null : previous,
    )
  }, [resetPasswordToken])

  const toast = useToastController()
  const { signInByToken } = useSession()
  const { setItem: setStorageToken } = useAsyncStorage(AUTH_TOKEN)
  const { data: themeParams, loading: themeParamsLoading } = useThemeParams()
  const hasStrongPasswordPolicy = useHasFeature(STRONG_PASSWORD_POLICY)
  const { handleResetPassword } = useUserPasswordReset()

  const school = themeParams?.school

  const passwordTooShort = useMemo(
    () => password.length > 0 && password.length < MIN_PASSWORD_LENGTH,
    [password],
  )

  const confirmationMismatch = useMemo(
    () => passwordConfirmation.length > 0 && password !== passwordConfirmation,
    [password, passwordConfirmation],
  )

  const isButtonDisabled = useMemo(() => {
    return (
      password.length < MIN_PASSWORD_LENGTH ||
      password !== passwordConfirmation ||
      isSubmitting
    )
  }, [password, passwordConfirmation, isSubmitting])

  const handlePasswordChange = (text: string) => {
    setPassword(text)
    setServerErrors([])
  }

  const handlePasswordConfirmationChange = (text: string) => {
    setPasswordConfirmation(text)
    setServerErrors([])
  }

  const handleSubmit = async () => {
    if (!resetPasswordToken || !school) return

    setIsSubmitting(true)
    try {
      const result = await handleResetPassword({
        resetPasswordToken,
        password,
        passwordConfirmation,
        schoolId: String(school.id),
      })

      if (!result || result.status === 'error') {
        setServerErrors(
          result?.errors?.length ? result.errors : [t('toast.error.message')],
        )
        return
      }

      if (
        result.status === 'invalid_token' ||
        result.status === 'expired_token'
      ) {
        setInvalidReason(result.status)
        return
      }

      if (result.token) {
        await setStorageToken(result.token)
      }

      if (isWeb) {
        window.location.href = result.redirectUrl ?? '/'
      } else {
        if (result.token) await signInByToken(result.token)
        router.replace('/')
      }
    } catch {
      toast.show(t('toast.error.title'), {
        message: t('toast.error.message'),
        type: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (themeParamsLoading) {
    return <LoadingScreen />
  }

  const isInvalid = Boolean(invalidReason) || !school

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={{ flexGrow: 1 }}
    >
      <GuestCard school={school}>
        <YStack width="100%" alignItems="center" gap="$size.spacing-xs">
          <Paragraph
            color="$text-primary"
            fontSize="$font-size-md"
            fontWeight={700}
            textAlign="center"
            lineHeight={30}
          >
            {isInvalid
              ? t(`${invalidReason ?? 'invalid_token'}.title`)
              : t('title')}
          </Paragraph>
          <Text
            color="$text-secondary"
            fontSize="$font-size-xs"
            fontFamily="$mulishRegular"
            textAlign="center"
            lineHeight={24}
          >
            {isInvalid
              ? t(`${invalidReason ?? 'invalid_token'}.description`)
              : t('subtitle')}
          </Text>
        </YStack>

        {isInvalid ? (
          <YStack width="100%" gap="$size.spacing-xxxs">
            <Button
              variant="primary"
              size="large"
              onPress={goToPasswordRequest}
              animation="quick"
              pressStyle={{ scale: 0.97 }}
            >
              {t('invalid.request-new')}
            </Button>
            <Button
              variant="secondary"
              size="large"
              onPress={goToLogin}
              animation="quick"
              pressStyle={{ scale: 0.97 }}
            >
              {t('invalid.back-to-login')}
            </Button>
          </YStack>
        ) : (
          <YStack width="100%" gap="$size.spacing-md">
            <YStack gap="$size.spacing-xs">
              <YStack gap="$size.spacing-nano">
                <Input
                  icon={faKey}
                  label={t('password.label')}
                  placeholder={t('password.placeholder')}
                  value={password}
                  onChangeText={handlePasswordChange}
                  secureTextEntry
                  errorMessage={
                    passwordTooShort ? t('password.error-too-short') : false
                  }
                  displayErrorIcon={false}
                />
                {hasStrongPasswordPolicy && (
                  <PasswordStrengthMeter password={password} />
                )}
              </YStack>

              <Input
                icon={faKey}
                label={t('passwordConfirmation.label')}
                placeholder={t('passwordConfirmation.placeholder')}
                value={passwordConfirmation}
                onChangeText={handlePasswordConfirmationChange}
                secureTextEntry
                errorMessage={
                  confirmationMismatch
                    ? t('passwordConfirmation.error-mismatch')
                    : false
                }
                displayErrorIcon={false}
              />

              {serverErrors.map((error) => (
                <Text
                  key={error}
                  color="$danger-300"
                  fontSize="$font-size-xxxs"
                  fontFamily="$mulishRegular"
                >
                  {error}
                </Text>
              ))}
            </YStack>

            <Button
              variant="primary"
              size="large"
              onPress={handleSubmit}
              disabled={isButtonDisabled}
              animation="quick"
              pressStyle={{ scale: 0.97 }}
              loading={isSubmitting}
            >
              {isSubmitting ? (
                <ActivityIndicator size="small" color="$neutral-0" />
              ) : (
                t('button.submit')
              )}
            </Button>
          </YStack>
        )}
      </GuestCard>
    </KeyboardAvoidingView>
  )
}
